/**
 * The line under the picker: how long the range runs and from when to when,
 * or, when the end is not after the start, what is wrong.
 */
import { AlertTriangle, Clock } from 'lucide-react';

import { formatDateKey } from './date-keys';
import { formatRangeLength, type DateTimeRange } from './date-time-range';
import { formatTimeOfDay } from './time-of-day';

export interface RangeSummaryProps {
  range: DateTimeRange;
  /** Whether the end is after the start, by the caller's rule. */
  inOrder: boolean;
  /** Said after the times, e.g. "All times Eastern". */
  zoneNote?: string;
}

export function RangeSummary({ range, inOrder, zoneNote }: RangeSummaryProps) {
  const length = inOrder ? formatRangeLength(range) : null;
  const { start, end } = range;
  return (
    <div
      role={length ? undefined : 'alert'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        fontSize: 12.5,
        minHeight: 20,
        lineHeight: 1.4,
        color: length ? 'var(--nb-muted)' : 'var(--nb-warn)',
        fontWeight: length ? 500 : 700,
      }}
    >
      {length ? (
        <Clock size={15} aria-hidden style={{ flex: '0 0 auto', marginTop: 1 }} />
      ) : (
        <AlertTriangle size={15} aria-hidden style={{ flex: '0 0 auto', marginTop: 1 }} />
      )}
      {length ? (
        <span>
          Runs <b style={{ color: 'var(--nb-ink)' }}>{length}</b>
          <span style={{ color: 'var(--nb-subtle)' }}>
            {' · '}
            {formatDateKey(start.date, { year: false })}, {formatTimeOfDay(start.minutes)} to{' '}
            {formatDateKey(end.date, { year: false })}, {formatTimeOfDay(end.minutes)}
            {zoneNote && ` · ${zoneNote}`}
          </span>
        </span>
      ) : (
        'The end has to be after the start.'
      )}
    </div>
  );
}
